import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Shield, X } from 'lucide-react';
import { useState } from 'react';
import { useAssignRole, useRemoveRole, useUserRoles } from '../../hooks/useUsers';

interface AssignRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
}

/**
 * Common Skewb Works Cognito groups. The backend accepts any group name so
 * users can also type a custom one.
 */
const SUGGESTED_ROLES = [
  'skewb-works-hr',
  'skewb-works-hr-super',
  'skewb-works-admin',
  'skewb-works-ops',
  'skewb-works-supervisor',
  'skewb-works-subcontractor',
  'skewb-works-mobile-user',
] as const;

export function AssignRoleDialog({ open, onOpenChange, userId }: AssignRoleDialogProps) {
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [customRole, setCustomRole] = useState('');

  const rolesQuery = useUserRoles(userId);
  const assign = useAssignRole();
  const remove = useRemoveRole();

  const cognitoGroups: string[] = rolesQuery.data?.cognitoGroups ?? [];

  const handleAssign = async () => {
    const groupName = selectedRole === 'custom' ? customRole.trim() : selectedRole;
    if (!groupName) return;
    await assign.mutateAsync({ userId, data: { groupName } });
    setSelectedRole('');
    setCustomRole('');
  };

  const handleRemove = async (groupName: string) => {
    await remove.mutateAsync({ userId, groupName });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Manage System Roles</DialogTitle>
          <DialogDescription>
            Assign or remove Cognito groups for this user. Groups control access to modules.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-muted-foreground" />
              <Label className="text-sm">Current Roles</Label>
            </div>
            <div className="min-h-[40px] flex flex-wrap gap-2 p-2 rounded-md border border-border bg-muted/30">
              {rolesQuery.isLoading && (
                <span className="text-xs text-muted-foreground">Loading…</span>
              )}
              {!rolesQuery.isLoading && cognitoGroups.length === 0 && (
                <span className="text-xs text-muted-foreground italic">No roles assigned.</span>
              )}
              {cognitoGroups.map((group) => (
                <Badge
                  key={group}
                  variant="outline"
                  className="bg-purple-50 dark:bg-purple-950/30 text-purple-800 dark:text-purple-200 border-purple-200 dark:border-purple-800 pl-2 pr-1 gap-1"
                >
                  {group}
                  <button
                    type="button"
                    onClick={() => handleRemove(group)}
                    disabled={remove.isPending}
                    className="ml-1 hover:bg-purple-200 dark:hover:bg-purple-800 rounded-sm p-0.5 disabled:opacity-50"
                    aria-label={`Remove ${group}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="role-select">Assign New Role</Label>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger id="role-select">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {SUGGESTED_ROLES.map((role) => (
                  <SelectItem key={role} value={role}>
                    {role}
                  </SelectItem>
                ))}
                <SelectItem value="custom">Custom group name…</SelectItem>
              </SelectContent>
            </Select>
            {selectedRole === 'custom' && (
              <Input
                placeholder="Enter Cognito group name"
                value={customRole}
                onChange={(e) => setCustomRole(e.target.value)}
              />
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            onClick={handleAssign}
            disabled={
              assign.isPending || !selectedRole || (selectedRole === 'custom' && !customRole.trim())
            }
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {assign.isPending ? 'Assigning…' : 'Assign Role'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
